/**
 * MetricChartWidget — SVG line chart for a single metric series.
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../../../lib/config";

interface MetricPoint {
  ts: string;
  value: number;
}

interface Props {
  config: Record<string, unknown>;
}

export function MetricChartWidget({ config }: Props) {
  const service = (config.service as string) ?? "";
  const metric = (config.metric as string) ?? "";
  const range = (config.range as string) ?? "1h";

  const [points, setPoints] = useState<MetricPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!service || !metric) {
      setLoading(false);
      return;
    }
    const params = new URLSearchParams({ service, name: metric, range });
    apiFetch(`/api/v1/telemetry/metrics/?${params}`)
      .then((r) => r.json())
      .then((d: MetricPoint[]) => setPoints(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [service, metric, range]);

  if (loading) return <div className="aptean-widget-skeleton" style={{ height: 80, background: "var(--aptean-surface-2)" }} />;
  if (error) return <p style={{ color: "var(--aptean-error, #e74c3c)", margin: 0 }}>Error: {error}</p>;
  if (!service || !metric) return <p style={{ color: "var(--aptean-text-secondary)", margin: 0 }}>Configure service and metric.</p>;
  if (points.length === 0) return <p style={{ color: "var(--aptean-text-secondary)", margin: 0 }}>No data.</p>;

  const W = 300;
  const H = 80;
  const vals = points.map((p) => p.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals) || 1;
  const toY = (v: number) => H - ((v - minV) / (maxV - minV)) * (H - 8) - 4;
  const toX = (i: number) => (i / (points.length - 1)) * W;

  const polyline = points
    .map((p, i) => `${toX(i).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(" ");

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H}`}
      className="aptean-metric-chart"
      aria-label={`${metric} metric chart`}
      style={{ display: "block" }}
    >
      <polyline
        points={polyline}
        fill="none"
        stroke="var(--aptean-accent, #54B3BE)"
        strokeWidth={2}
      />
    </svg>
  );
}
